import type { ComponentType } from "react";
import { coreEvents, type CoreEventName, type CoreEventMap } from "../../domain/events/bus";

/**
 * Module registry.
 *
 * A module is self-contained, independently toggleable, and safe to disable at
 * runtime without breaking a sale. Disabling every module MUST leave a working
 * till — that is a test (tests/e2e/modules-off), not an aspiration.
 *
 * See contracts/module-contract.md.
 */

/** Named extension points core renders without knowing their contents. */
export type SlotId =
  | "till.item.badge"
  | "till.item.detail"
  | "till.cart.footer"
  | "till.postSale"
  | "till.settings"
  | "terminal.actions"
  | "till.shiftClose.summary"
  /** Per row of the recent-sales list: { saleId, shiftId, shiftOpen, status, loadLines }. */
  | "till.recentSale.actions"
  | "backoffice.nav"
  | "backoffice.dashboard"
  | "receipt.line"
  | "receipt.footer";

export interface SlotProps {
  [key: string]: unknown;
}

export interface RouteDefinition {
  path: string;
  label: string;
  component: ComponentType;
}

export interface MigrationRef {
  id: string;
  file: string;
}

export interface ModuleContext {
  moduleId: string;
}

export type ModuleSubscribers = {
  [K in CoreEventName]?: (event: CoreEventMap[K]) => void | Promise<void>;
};

export interface ModuleDefinition {
  id: string;
  name: string;
  version: string;
  subscribers?: ModuleSubscribers;
  slots?: Partial<Record<SlotId, ComponentType<SlotProps>>>;
  routes?: RouteDefinition[];
  migrations?: MigrationRef[];
  onEnable?(ctx: ModuleContext): Promise<void> | void;
  onDisable?(ctx: ModuleContext): Promise<void> | void;
}

interface RegistryEntry {
  definition: ModuleDefinition;
  enabled: boolean;
  unsubscribers: Array<() => void>;
}

export class ModuleRegistry {
  readonly #entries = new Map<string, RegistryEntry>();

  register(definition: ModuleDefinition): void {
    if (this.#entries.has(definition.id)) {
      throw new Error(`Module already registered: ${definition.id}`);
    }
    this.#entries.set(definition.id, { definition, enabled: false, unsubscribers: [] });
  }

  async enable(moduleId: string): Promise<void> {
    const entry = this.#require(moduleId);
    if (entry.enabled) return;

    const { subscribers } = entry.definition;
    if (subscribers) {
      for (const [event, handler] of Object.entries(subscribers)) {
        if (!handler) continue;
        entry.unsubscribers.push(
          coreEvents.on(
            event as CoreEventName,
            moduleId,
            handler as (e: CoreEventMap[CoreEventName]) => void | Promise<void>,
          ),
        );
      }
    }

    await entry.definition.onEnable?.({ moduleId });
    entry.enabled = true;
  }

  /**
   * Disabling NEVER destroys data (FR-085). It detaches behaviour only; every
   * row the module wrote remains, and re-enabling restores full function.
   */
  async disable(moduleId: string): Promise<void> {
    const entry = this.#require(moduleId);
    if (!entry.enabled) return;

    for (const off of entry.unsubscribers) off();
    entry.unsubscribers = [];
    coreEvents.offModule(moduleId);

    await entry.definition.onDisable?.({ moduleId });
    entry.enabled = false;
  }

  isEnabled(moduleId: string): boolean {
    return this.#entries.get(moduleId)?.enabled ?? false;
  }

  /** Ids of enabled modules — recorded on every sale so history stays readable (FR-088). */
  activeModules(): string[] {
    return [...this.#entries.values()]
      .filter((e) => e.enabled)
      .map((e) => e.definition.id)
      .sort();
  }

  /** Components registered into a slot by ENABLED modules only. */
  slotComponents(slot: SlotId): Array<{ moduleId: string; component: ComponentType<SlotProps> }> {
    const out: Array<{ moduleId: string; component: ComponentType<SlotProps> }> = [];
    for (const entry of this.#entries.values()) {
      if (!entry.enabled) continue;
      const component = entry.definition.slots?.[slot];
      if (component) out.push({ moduleId: entry.definition.id, component });
    }
    return out;
  }

  routes(): RouteDefinition[] {
    return [...this.#entries.values()]
      .filter((e) => e.enabled)
      .flatMap((e) => e.definition.routes ?? []);
  }

  all(): ModuleDefinition[] {
    return [...this.#entries.values()].map((e) => e.definition);
  }

  #require(moduleId: string): RegistryEntry {
    const entry = this.#entries.get(moduleId);
    if (!entry) throw new Error(`Unknown module: ${moduleId}`);
    return entry;
  }
}

export const modules = new ModuleRegistry();
